import {createContext} from 'react';
import {Subject} from 'rxjs';

export const GlobalErrorContext = createContext<Subject<Error>>(new Subject<Error>());

// Enhanced error context for better error handling
export interface ErrorDetails {
    message: string;
    stack?: string;
    networkError?: any;
    graphQLErrors?: any[];
    isSessionTimeout?: boolean;
    authProvider?: 'okta' | 'azure';
}

export const extractErrorDetails = (error: any): ErrorDetails => {
    if (!error) {
        return { message: 'An unknown error occurred' };
    }

    // Handle Error objects
    if (error instanceof Error) {
        const details: ErrorDetails = { 
            message: error.message || 'An error occurred',
            stack: error.stack 
        };

        // Check for Apollo/GraphQL errors
        if ('networkError' in error) {
            details.networkError = (error as any).networkError;
            if ((error as any).networkError?.message) {
                details.message = (error as any).networkError.message;
            }
        }

        if ('graphQLErrors' in error && Array.isArray((error as any).graphQLErrors)) {
            details.graphQLErrors = (error as any).graphQLErrors;
            const gqlErrorMessages = (error as any).graphQLErrors
                .map((err: any) => err.message)
                .filter(Boolean);
            if (gqlErrorMessages.length > 0) {
                details.message = gqlErrorMessages.join('; ');
            }
        }

        return details;
    }

    // Handle string errors
    if (typeof error === 'string') {
        return { message: error };
    }

    // Handle objects with message property
    if (error && typeof error === 'object') {
        if (error.message) {
            const details: ErrorDetails = { message: error.message };
            if (error.stack) details.stack = error.stack;
            if (error.networkError) details.networkError = error.networkError;
            if (error.graphQLErrors) details.graphQLErrors = error.graphQLErrors;
            return details;
        }

        // Handle response-like objects (e.g., fetch errors)
        if (error.status && error.statusText) {
            return { 
                message: `${error.status}: ${error.statusText}`,
                stack: error.data || JSON.stringify(error)
            };
        }

        // Handle generic objects
        try {
            const stringified = JSON.stringify(error);
            return { 
                message: stringified !== '{}' ? stringified : 'An error occurred',
                stack: error.stack
            };
        } catch {
            return { message: 'An error occurred' };
        }
    }

    return { message: 'An error occurred' };
};
